import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { formatMovieTitle } from '../utils/titleFormatting';
import { THEATERS, THEATER_CHAINS, THEATER_LOCATIONS, LOCATIONS, getTicketLink, OUTDOOR_THEATER_IDS } from '../constants';
import { VEEZI_PREVIEW, VEEZI_PREVIEW_CINEMA, VEEZI_PREVIEW_SUPPRESS, VEEZI_PREVIEW_MULTI, loadVeeziPreview } from '../utils/veeziPreview';
import { posterSrc } from '../utils/poster';
import { isWatchlistMatch } from '../utils/watchlistMatching';
import { supabaseMovies, supabaseShowTimes, supabaseNewsletter } from '../supabase';
import { slugify } from '../utils/slugify';
import { isExclusionActive } from '../utils/moderationHelpers';
import WatchlistHeart from '../components/WatchlistHeart';
import AuthModal from '../components/AuthModal';
import { subscribeToNewsletter } from '../utils/newsletterSignup';
import { useAuth } from '../contexts/AuthContext';


// Shared title badges (celluloid 🎞️ + a gold "SHORT" / "SHORTS PROGRAM" tag), used by
// the month, week, and list renders so they can't drift apart.
function renderBadges(hasFilmFormat, isShort, isProgram, programKind) {
  const tag = (label) => (
    <span className="short-tag" style={{
      fontSize: '0.68em', fontWeight: 700, color: '#8a6d1f',
      border: '1px solid #cbb26a', borderRadius: '3px', padding: '0 4px',
      marginLeft: '5px', verticalAlign: 'middle', textTransform: 'uppercase',
      letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>{label}</span>
  );
  return (
    <>
      {hasFilmFormat && '🎞️'}
      {isShort && tag('Short')}
      {isProgram && tag(programKind === 'conference' ? 'Conference' : programKind === 'featureWithShorts' ? 'Feature + Shorts' : 'Shorts Program')}
    </>
  );
}

// ONE title+year computation for ALL views — list, week, and month MUST render
// identically (Josh 2026-07-13; they had drifted: month scraped years out of venue
// billing text and honored admin customTitles that list/week ignored).
// Title: session review customTitle → admin moderation customTitle → the grouped
// entry title (movie-record title primary). Year: customTitle-embedded → oldest
// release date → release_year. NEVER scraped from billing text (fest years like
// "(EVANGELION 30th Movie Fest) (2007)" produced wrong month-view years).
function displayTitleYear(entry, reviewStatus) {
  const custom = reviewStatus?.customTitle || entry.customTitle || null;
  const base = custom || entry.title || '';
  const cleanTitle = base.replace(/\s*\(\d{4}\)/g, '').trim();
  let releaseYear = null;
  if (custom) {
    const m = custom.match(/\((\d{4})\)/);
    if (m) releaseYear = parseInt(m[1]);
  }
  if (!releaseYear && entry.release_dates && Object.keys(entry.release_dates).length > 0) {
    const d = Object.values(entry.release_dates).flat().map(r => r && r.date).filter(Boolean).sort()[0];
    if (d) releaseYear = new Date(d).getFullYear();
  }
  if (!releaseYear && entry.release_year) releaseYear = parseInt(entry.release_year);
  if (releaseYear && (isNaN(releaseYear) || releaseYear < 1870 || releaseYear > 2100)) releaseYear = null;
  return { cleanTitle, releaseYear, displayTitle: releaseYear ? `${cleanTitle} (${releaseYear})` : cleanTitle };
}

// Series (e.g. Anthology's "Experimental Westerns, Part 1") IS captured in the
// showtimes.series DB column by the crawlers for future use — displayed on the
// site or exposed via the endpoint later — but kept hidden in the UI for now.
// Flip to true to surface the series filter + tags.
const SERIES_UI_ENABLED = false;
// WHITELIST exception (Josh 8/5 + 8/12): series stay hidden site-wide EXCEPT
// named ones that earn a visible gold pill (Silent Clowns at Cobble Hill).
const SERIES_UI_WHITELIST = new Set(['Silent Clowns Film Series']);


// Isolated search input: owns its own local state so each keystroke
// re-renders only this tiny component, not the whole CalendarView tree
// (which on prod feels like input lag when reconciling 21k showtimes per
// letter). Calls onChange with the typed value after 250ms idle so the
// parent's URL-param + filter update runs only when typing pauses.
const DebouncedSearchInput = React.memo(function DebouncedSearchInput({ initialValue, onChange, placeholder, className }) {
  const [value, setValue] = useState(initialValue || '');
  // Track whether the user is actively typing — only debounce-fire onChange
  // for user input, not when initialValue changes from outside (back/forward).
  const skipNextDebounce = useRef(true);
  useEffect(() => {
    if (initialValue !== value) {
      skipNextDebounce.current = true;
      setValue(initialValue || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);
  useEffect(() => {
    if (skipNextDebounce.current) {
      skipNextDebounce.current = false;
      return;
    }
    const t = setTimeout(() => onChange(value), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className={className}
    />
  );
});

// Movie link component — uses /films/:slug when slug is available
const MovieLink = ({ movieId, slug, state, children }) => {
  const filmUrl = slug ? `/films/${slug}` : `/movie/${movieId}`;
  const handleClick = (e) => {
    e.preventDefault();

    // Use different URLs for development vs production
    const movieUrl = process.env.NODE_ENV === 'development'
      ? filmUrl
      : (slug ? filmUrl : `/.netlify/functions/og-movie-page?id=${movieId}`);

    // Detect mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    if (isMobile) {
      sessionStorage.setItem(`movieState_${movieId}`, JSON.stringify(state));
      window.location.href = movieUrl;
    } else {
      const win = window.open(movieUrl, '_blank');
      if (win) {
        win.opener.movieState = state;
      }
    }
  };

  return (
    <a
      href={filmUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

// (MOVIE_ID_OVERRIDES removed with the client IST fallback 2026-07-13 — server-side
// overrides live in netlify/functions/lib/movieIdOverrides.js.)

// Add this function near the top of the file with other utility functions
const createGoogleCalendarLink = ({ title, startTime, location }) => {
  const date = new Date(startTime);
  const endTime = new Date(date.getTime() + (3 * 60 * 60 * 1000)); // Add 3 hours for movie duration
  
  const formatDate = (date) => date.toISOString().replace(/-|:|\.\d+/g, '');
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatDate(date)}/${formatDate(endTime)}`,
    location: location,
    details: `Movie screening of ${title} at ${location}`
  });

  return `https://www.google.com/calendar/render?${params.toString()}`;
};

function CalendarView(props) {
  const {isLoading, setIsLoading} = props;
  const { user, profile, watchlist, savedFilters, saveFilter, deleteFilter, supabase } = useAuth();

  // (The mid-July email-alert promo banner was RETIRED 9/2 — Josh: "its been
  // up there a while." The Watchlist page checkbox remains the real control;
  // the newsletter-ask banner below is now the single top-of-page banner.)
  // (The 8/28 merged-capture banner was DELETED 9/2 — superseded by the
  // cross-sell design: newsletter checkbox in AuthModal signup + account CTA
  // after a footer subscribe. Its backend, netlify/functions/subscribe.js,
  // stays untracked/parked.)

  // NEWSLETTER ASK banner (9/2): ANY signed-in user not on the newsletter
  // list gets one dismissible ask — this is both the visible net for the
  // silent OAuth checkbox hand-off (when it fails, nothing else surfaces) AND
  // the bridge to the ~139 existing account holders who never subscribed
  // (Josh 9/2: "how do we get to the people who have already made accounts").
  // Dismiss or answer is final per account.
  const [welcomeAsk, setWelcomeAsk] = useState('hidden'); // hidden | show | busy | done
  useEffect(() => {
    if (!user?.email) return;
    try { if (localStorage.getItem('welcomeNewsletterAsk:' + user.id)) return; } catch { return; }
    // Small delay so a just-fired silent subscribe can land first.
    const t = setTimeout(async () => {
      const { data } = await supabase.from('newsletter_emails').select('id').eq('email', user.email).maybeSingle();
      if (!data) setWelcomeAsk('show');
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const answerWelcomeAsk = async (yes) => {
    try { localStorage.setItem('welcomeNewsletterAsk:' + user.id, 'answered'); } catch { /* fine */ }
    if (!yes) { setWelcomeAsk('hidden'); return; }
    setWelcomeAsk('busy');
    const r = await subscribeToNewsletter(user.email);
    setWelcomeAsk(r.ok ? 'done' : 'hidden');
  };

  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);

  // Newsletter signup
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState(null);
  // Cross-sell (Josh 9/2): after a successful subscribe, logged-out visitors
  // get a one-click account offer with their email prefilled.
  const [subscribedEmail, setSubscribedEmail] = useState('');
  const [showFooterAuthModal, setShowFooterAuthModal] = useState(false);

  // Header "Newsletter" link lands on /#newsletter — scroll to the footer form.
  useEffect(() => {
    if (window.location.hash === '#newsletter') {
      setTimeout(() => document.getElementById('newsletter')?.scrollIntoView({ behavior: 'smooth' }), 400);
    }
  }, []);

  // Newsletter-CTA deep link (Josh's Sunday issue promotes accounts):
  // filmrevivalnyc.com/?signup=1 opens the Create Account window on arrival.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === '1' && !user) setShowFooterAuthModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleNewsletterSubmit(e) {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterSubmitting(true);
    setNewsletterMessage(null);
    try {
      const res = await fetch('/.netlify/functions/subscribe-newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail }),
      });
      const data = await res.json();
      if (data.success) {
        setNewsletterMessage({
          type: 'success',
          text: data.alreadySubscribed ? "You're already subscribed!" : 'Thanks for subscribing!',
        });
        setSubscribedEmail(newsletterEmail);
        setNewsletterEmail('');
      } else {
        setNewsletterMessage({ type: 'error', text: data.error || 'Subscription failed' });
      }
    } catch (err) {
      // Fallback to direct Supabase insert if the function is unreachable
      const result = await supabaseNewsletter.subscribe(newsletterEmail);
      if (result.success) {
        setNewsletterMessage({ type: 'success', text: 'Thanks for subscribing!' });
        setSubscribedEmail(newsletterEmail);
        setNewsletterEmail('');
      } else {
        setNewsletterMessage({ type: 'error', text: result.error || 'Subscription failed' });
      }
    }
    setNewsletterSubmitting(false);
  }

  // URL state management
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Helper functions to read/write URL params
  const getURLParam = (key, defaultValue) => searchParams.get(key) || defaultValue;
  const setURLParam = (key, value) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
      return newParams;
    });
  };
  
  // calendarData: { date: { movieId: { movie_id, title, showtimes: [{ start_at, theater, theaterId }] } } }
  const [calendarData, setCalendarData] = useState({});
  const [moviePosters] = useState({}); // setter removed with fetchMissingPosters (2026-07-13)
  // useEffect(() => {console.log(calendarData)}, [calendarData])
  const [selectedDate, setSelectedDate] = useState(null);
  
  // For controlling displayed month.
  const today = new Date();
  const initialMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  
  // URL-synced state variables - useMemo to ensure reactivity
  const viewMode = useMemo(() => getURLParam('view', 'list'), [searchParams]);
  const setViewMode = (mode) => setURLParam('view', mode);
  const compactMode = useMemo(() => getURLParam('compact', '0') === '1', [searchParams]);
  const setCompactMode = (on) => setURLParam('compact', on ? '1' : null);
  // List view organisation (9/2, Josh: "should have multiple organize options
  // i.e. by theater, alphabetical by title"). 'theater' keeps the original
  // venue-grouped layout; 'title' is one card per FILM with its venues inside.
  const listSort = useMemo(() => getURLParam('sort', 'theater'), [searchParams]);
  const setListSort = (v) => setURLParam('sort', v === 'theater' ? null : v);
  const onlyWatchlist = useMemo(() => getURLParam('watchlist', '0') === '1', [searchParams]);
  const setOnlyWatchlist = (on) => setURLParam('watchlist', on ? '1' : null);
  // Movie-id-first matcher: any saved row whose movie_id matches counts as a
  // watchlist hit; title is the fallback for legacy rows without a movie_id.
  const isEntryOnWatchlist = useMemo(() => {
    const items = watchlist || [];
    return ({ movie_id, title }) => items.some(w => isWatchlistMatch({
      watchlistMovieId: w.movie_id,
      watchlistTitle: w.movie_title,
      showMovieId: movie_id,
      showTitle: title,
    }));
  }, [watchlist]);
  // Seasonal outdoor screenings — toggle hides/reveals a parallel calendar.
  // When OFF, outdoor showtimes are stripped from all entries. When ON, only
  // outdoor showtimes are shown.
  const onlyOutdoor = useMemo(() => getURLParam('outdoor', '0') === '1', [searchParams]);
  const setOnlyOutdoor = (on) => setURLParam('outdoor', on ? '1' : null);
  // The toggle button only appears when at least one outdoor screening exists
  // within the next 30 days. Off-season it disappears entirely.
  const hasUpcomingOutdoorScreenings = useMemo(() => {
    // Localhost dev short-circuit: render the button locally without needing
    // to populate Supabase from a feature branch (the shared DB is read by
    // the live site, so we keep outdoor rows out of it until the feature
    // ships).
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return true;
    }
    if (!calendarData) return false;
    const cutoff = Date.now() + 30 * 24 * 60 * 60 * 1000;
    for (const dateStr in calendarData) {
      const movies = calendarData[dateStr] || {};
      for (const m of Object.values(movies)) {
        for (const st of (m.showtimes || [])) {
          if (st.is_outdoor) {
            const t = new Date(st.start_at).getTime();
            if (t > Date.now() && t < cutoff) return true;
          }
        }
      }
    }
    return false;
  }, [calendarData]);
  
  const searchTerm = useMemo(() => getURLParam('search', ''), [searchParams]);
  // Search input state is owned by an isolated component below
  // (DebouncedSearchInput) so each keystroke re-renders only that input,
  // not the entire 2500-line CalendarView tree. CalendarView itself only
  // sees the debounced URL-derived searchTerm, which changes after typing
  // pauses for ~250ms.
  const setSearchTerm = (term) => setURLParam('search', term);
  
  // Parse dates from URL or use defaults
  const parseDateFromURL = (key, defaultDate) => {
    const dateStr = getURLParam(key, null);
    if (dateStr) {
      // Parse as local date (not UTC) to avoid timezone issues
      const [year, month, day] = dateStr.split('-').map(Number);
      const parsed = new Date(year, month - 1, day);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return defaultDate;
  };
  
  const formatDateForURL = (date) => {
    // Format as local date YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Current month from URL or default
  const monthParam = getURLParam('month', null);
  const currentMonth = monthParam ? (() => {
    const [year, month] = monthParam.split('-').map(Number);
    return new Date(year, month - 1, 1);
  })() : initialMonth;
  const setCurrentMonth = (date) => {
    const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    setURLParam('month', monthStr);
  };
  
  // Current week from URL or default
  const currentWeek = parseDateFromURL('week', new Date());
  const setCurrentWeek = (date) => setURLParam('week', formatDateForURL(date));
  
  // Selected theaters from URL
  const selectedTheaters = (() => {
    const theatersParam = getURLParam('theaters', '');
    return theatersParam ? theatersParam.split(',').filter(Boolean) : [];
  })();
  const setSelectedTheaters = (theaters) => {
    setURLParam('theaters', theaters.length > 0 ? theaters.join(',') : null);
  };

  // Add this state near the top with other state declarations
  const [showNoReleaseDateMovies, setShowNoReleaseDateMovies] = useState(false);

  // Add these new state variables
  const [reviewedMovies, setReviewedMovies] = useState(() => {
    // Load previously reviewed movies from localStorage (parse guarded — corrupt
    // localStorage must not crash the calendar mount; 2026-07-21 hardening pass)
    try {
      const saved = localStorage.getItem('filmRevivalReviewedMovies');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  // Selected locations from URL
  const selectedLocations = (() => {
    const locationsParam = getURLParam('locations', '');
    return locationsParam ? locationsParam.split(',').filter(Boolean) : [];
  })();
  const setSelectedLocations = (locations) => {
    setURLParam('locations', locations.length > 0 ? locations.join(',') : null);
  };
  
  // Film format filter from URL
  const filmFormatFilter = getURLParam('format', 'all');
  const setFilmFormatFilter = (format) => setURLParam('format', format === 'all' ? null : format);
  
  // Add these to your state declarations
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const locationDropdownRef = useRef(null);

  // Add these to your existing state declarations

  // First, make sure we have the state for the theater dropdown
  const [isTheaterDropdownOpen, setIsTheaterDropdownOpen] = useState(false);
  const theaterDropdownRef = useRef(null);

  // Film format dropdown state
  const [isFilmDropdownOpen, setIsFilmDropdownOpen] = useState(false);
  const filmDropdownRef = useRef(null);

  // Series filter from URL
  const selectedSeries = (() => {
    const param = getURLParam('series', '');
    return param ? param.split(',').filter(Boolean) : [];
  })();
  const setSelectedSeries = (series) => {
    setURLParam('series', series.length > 0 ? series.join(',') : null);
  };
  const [isSeriesDropdownOpen, setIsSeriesDropdownOpen] = useState(false);
  const seriesDropdownRef = useRef(null);
  const filtersDropdownRef = useRef(null);

  // Compute available series dynamically from current calendar data.
  // Empty while SERIES_UI_ENABLED is false → hides the filter dropdown and
  // neutralizes series filtering, while the data stays captured in the DB.
  const availableSeries = SERIES_UI_ENABLED ? [...new Set(
    Object.values(calendarData)
      .flatMap(dateMovies => Object.values(dateMovies))
      .flatMap(movie => movie.showtimes || [])
      .map(st => st.series)
      .filter(Boolean)
  )].sort() : [];

  // SMART SEARCH (Josh 8/14): match against everything a cinephile might type —
  // film title (record + billing + admin custom), director, genres, year,
  // theater names, and series names. Multi-word queries are AND-ed
  // ("coppola film forum" → Coppola films at Film Forum). Each token must
  // appear somewhere in the entry's combined haystack.
  const entryMatchesSearch = (entry, search) => {
    if (!search) return true;
    const parts = [entry.title, entry.customTitle, entry.director, entry.release_year];
    if (Array.isArray(entry.genres)) parts.push(...entry.genres.map(g => (g && g.name) || g));
    if (Array.isArray(entry.cast_list)) parts.push(...entry.cast_list.map(a => (a && a.name) || a));
    for (const st of entry.showtimes || []) {
      parts.push(st.theater, st.series, st.cinema_movie_title);
    }
    const haystack = parts.filter(Boolean).join(' | ').toLowerCase();
    const esc = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const q = search.toLowerCase().trim();
    const tokens = q.split(/\s+/).filter(Boolean);
    // PHRASE first: the whole query as a contiguous word-boundary match
    // ("la la land" → La La Land, never Land of the Dead).
    if (new RegExp('(?:^|[^a-z0-9])' + tokens.map(esc).join('\\s+')).test(haystack)) return true;
    // Cross-field token-AND fallback ("coppola forum") — but ONLY when every
    // token is 3+ chars. Short tokens ("la") prefix-match half the database
    // across cast/theater fields, which made two rounds of "too broad" (8/28).
    if (tokens.length > 1 && tokens.some(t => t.length < 3)) return false;
    return tokens.every(tok => new RegExp('(?:^|[^a-z0-9])' + esc(tok)).test(haystack));
  };


  // Add this helper function near the top of the file, after the initial constants
  const getOldestReleaseDate = (releaseDates) => {
    if (!releaseDates) return null;
    
    try {
      // Prioritize US release date if available
      const usReleases = releaseDates['US'];
      if (usReleases && usReleases.length > 0) {
        const usDates = usReleases
          .map(release => release.date)
          .sort((a, b) => new Date(a) - new Date(b));
        
        if (usDates[0]) {
          return usDates[0];
        }
      }
      
      // Fallback to oldest worldwide release
      const dates = Object.values(releaseDates)
        .flat()
        .map(release => release.date)
        .sort((a, b) => new Date(a) - new Date(b));
      
      return dates[0];
    } catch (error) {
      console.error('Error in getOldestReleaseDate:', error, releaseDates);
      return null;
    }
  };

  // Modified fetchShowtimesForTheater with simple debug logging
  // After any changes in this func, need to be copied to netlify/functions/sync-movie.js
  const fetchShowtimesForTheater = async (theater, timeFrom, timeTo, preloadedShowtimes = null) => {
    // service functions for delete all data in db
      // await supabaseShowTimes.deleteAll();
      // await supabaseMovies.deleteAll();

    try {

      // Veezi-preview venue (local dev): serve purely from the static export —
      // never Supabase, never the IST fallback below (IST mis-matches this
      // venue's programming, which is the whole reason for the Veezi crawler).
      // Multi-venue preview (Fathom QA): every chain venue serves its OWN slice of
      // the preview file (real cinema_ids, real names) — never Supabase.
      if (VEEZI_PREVIEW_MULTI.has(String(theater.id))) {
        const preview = await loadVeeziPreview();
        return (preview?.showtimes || []).filter(st => String(st.cinema_id) === String(theater.id));
      }

      if (VEEZI_PREVIEW_CINEMA && theater.id === VEEZI_PREVIEW_CINEMA) {
        const preview = await loadVeeziPreview();
        return preview?.showtimes || [];
      }

      // Multi-venue preview (e.g. the AMC+Regal cleanup): the OTHER cinemas in the
      // group are collapsed into the preview venue above — blank their live rows so
      // dirty DB data doesn't bleed in beside the cleaned slate.
      if (VEEZI_PREVIEW_SUPPRESS.has(String(theater.id))) return [];

      // Use pre-loaded batch data if available, otherwise query individually
      const showtimesFromDB = preloadedShowtimes !== null
        ? preloadedShowtimes
        : await supabaseShowTimes.getAllWithinTimeFrameByTheater(theater.id, timeFrom, timeTo);

      // IST FALLBACK REMOVED (2026-07-13). This used to fetch IST and WRITE it to
      // Supabase whenever a theater's DB result was empty — meaning any VISITOR'S
      // BROWSER could re-pollute crawler-owned venues with stale IST junk. Worse,
      // the batch query is row-capped, so theaters past the cap looked "empty" and
      // got mass-rewritten (the recurring Syndicated FIFA / museum-dup mystery).
      // Data ownership now lives server-side ONLY: per-theater crawlers + the
      // sync-showtimes/cleanup-chains pair for AMC/Regal. An empty venue renders
      // empty until its owner's next cron — never from IST via the client.
      return showtimesFromDB || [];
    } catch (err) {
      console.error("Error fetching showtimes for", theater.name, err);
      return [];
    }
  };

  // (fetchMissingPosters removed 2026-07-13 — it wrote YouTube trailer thumbnails
  // into movies.poster_path from VISITORS' BROWSERS, re-minting the low-res YT
  // poster class the TMDB backfill retired. Posters are server-side now:
  // enrich-movies + the crawlers. moviePosters state remains for preview data.)

  // Modified fetchMovieDetailsInBatches with improved batching and caching
  const fetchMovieDetailsInBatches = async (movieIds) => {

    // await supabaseMovies.deleteAll();
    
    const storedMovies = {};
    let unstoredMovieIds = [];
    const allStoredMoviesInDB = await supabaseMovies.getByIds(movieIds);

    if (allStoredMoviesInDB && allStoredMoviesInDB[0]) {
      for (const id of movieIds) {
        const foundInDb = allStoredMoviesInDB.find(el => el.id === id)
        if (foundInDb && foundInDb.id) {
          storedMovies[id] = foundInDb;
        } else {
          unstoredMovieIds.push(id);
        }
      }
    } else {
      unstoredMovieIds = [...movieIds];
    }

    // Veezi-preview movies (local dev): seed records from the static export so
    // the venue's ids are treated as known. (The IST lookup + client write that
    // used to follow were removed 2026-07-13 — server-side owns movie records.)
    // The PREVIEW record (fresh crawler output) wins on the scalar fields it
    // provides, so the preview mirrors what prod will show post-ship (e.g. a
    // placeholder whose stale prod row lacks a year but the fresh record has one).
    // moderation_info is DEEP-merged with admin (prod) fields winning — the live
    // crawler never overwrites an admin customTitle/exclusion, so preview must not
    // either (a plain spread would mask a prod exclusion behind the auto customTitle).
    if (VEEZI_PREVIEW) {
      const preview = await loadVeeziPreview();
      const previewMovies = preview?.movies || {};
      const asObj = (mi) => (typeof mi === 'string' ? (() => { try { return JSON.parse(mi); } catch { return null; } })() : mi) || null;
      for (const id of Object.keys(previewMovies)) {
        const prod = storedMovies[id] || {};
        const merged = { ...prod, ...previewMovies[id] };
        let prodMi = asObj(prod.moderation_info);
        // Prod records may still carry AUTO-stamped customTitles from the old
        // foreign-title policy (titleAuto:true). The live crawlers strip those on
        // their next run; strip them here too so the preview shows the post-ship
        // state. Admin customTitles (no titleAuto) still win as before.
        if (prodMi?.titleAuto && prodMi?.customTitle) {
          const { customTitle, titleAuto, ...rest } = prodMi;
          prodMi = Object.keys(rest).length ? rest : null;
        }
        const previewMi = asObj(previewMovies[id].moderation_info);
        if (prodMi || previewMi) merged.moderation_info = { ...(previewMi || {}), ...(prodMi || {}) };
        storedMovies[id] = merged;
      }
      unstoredMovieIds = unstoredMovieIds.filter(id => !previewMovies[id]);
    }

    // IST MINTING REMOVED (2026-07-13). Ids missing from the movies table used to be
    // fetched from IST here and WRITTEN to Supabase from the visitor's browser —
    // minting records with IST-era titles + YouTube-thumbnail posters (the stale
    // foreign-title + low-res poster classes). Movie records are server-side only
    // now (crawlers/matchMovie + enrich-movies). Missing ids simply render without
    // a detail record — the orphan class the fleet keeps at ~0.
    return storedMovies;
  };

  useEffect(() => {
    const fetchAllShowtimes = async () => {
      setIsLoading(true);
      let movieDetails = {};
      try {
        const now = new Date();

        // PHASE 1: Fast initial load — single query for today only (~2000 rows)
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

        const todayShowtimes = await supabaseShowTimes.getAllWithinTimeFrame(
          todayStart.toISOString(),
          todayEnd.toISOString()
        );

        let allShowtimes = todayShowtimes || [];

        // Veezi-preview (local dev): the global today-query returns the previewed
        // venue's OLD Supabase rows — replace them with the Veezi export so the
        // first paint already reflects Veezi, matching the Phase-2 per-theater load.
        if (VEEZI_PREVIEW_CINEMA) {
          const preview = await loadVeeziPreview();
          allShowtimes = allShowtimes.filter(
            st => st.theaterId !== VEEZI_PREVIEW_CINEMA && st.cinema_id !== VEEZI_PREVIEW_CINEMA
              // Multi-venue preview: also drop the other grouped cinemas' live rows.
              && !VEEZI_PREVIEW_SUPPRESS.has(String(st.theaterId)) && !VEEZI_PREVIEW_SUPPRESS.has(String(st.cinema_id))
          );
          if (preview?.showtimes) allShowtimes = allShowtimes.concat(preview.showtimes);
        }

        if (allShowtimes.length > 0) {
          const uniqueMovieIds = [...new Set(
            allShowtimes.map(st => st && st.movie_id)
          )];

          movieDetails = await fetchMovieDetailsInBatches(uniqueMovieIds)
            .catch(err => {
              console.error('Error fetching movie details:', err);
              return {};
            });

          await fillReviewedMovies(movieDetails);

          const calendarMap = groupShowtimesByDate(allShowtimes, movieDetails);
          setCalendarData(calendarMap);
        }
      } catch (error) {
        console.error('Error in fetchAllShowtimes:', error);
      } finally {
        setIsLoading(false);
      }


      // PHASE 2: Background prefetch ~6-month range for day navigation.
      // 90 days was too short — outdoor summer series (BBP MWAV, NYC Parks) run
      // into late September, beyond a June-anchored 90-day window. 180 days
      // covers the full summer-outdoor season plus a bit of fall.
      try {
        const now = new Date();
        const firstOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const initialEnd = new Date(now.getTime() + (180 * 24 * 60 * 60 * 1000));

        const fullShowtimes = (await Promise.all(
          THEATERS.map(theater =>
            fetchShowtimesForTheater(
              theater,
              firstOfCurrentMonth.toISOString(),
              initialEnd.toISOString(),
              null
            ).catch(() => [])
          )
        )).flat();

        if (fullShowtimes.length > 0) {
          const fullMovieIds = [...new Set(fullShowtimes.map(st => st?.movie_id))];
          const fullMovieDetails = await fetchMovieDetailsInBatches(fullMovieIds).catch(() => ({}));
          await fillReviewedMovies(fullMovieDetails);

          const fullCalendarMap = groupShowtimesByDate(fullShowtimes, fullMovieDetails);
          setCalendarData(fullCalendarMap);
        }
      } catch (err) {
        console.error('Background full-range fetch error:', err);
      }
    };

    fetchAllShowtimes();
  }, []);
 
  // fill Reviewed Movies from movieDetails
  const fillReviewedMovies = (movieDetails) => {
    const prefilledReviewedMovies = {}
    for (const movie of Object.values(movieDetails)) {
      if (movie.moderation_info) {
        prefilledReviewedMovies[movie.id] = movie.moderation_info
      }
    }
    setReviewedMovies(prev => ({ ...prev, ...prefilledReviewedMovies }));
  }

  // Modify the groupShowtimesByDate function to filter out past showtimes
  const groupShowtimesByDate = (showtimes, movieDetails) => {
    const calendarMap = {};
    const lastYearStart = new Date(new Date().getFullYear() - 1, 0, 1);
    const now = new Date();

    showtimes.forEach((st, i) => {
      if (!st.start_at) return;
      
      // Skip if this showtime is in the past
      if (new Date(st.start_at) < now) return;
      
      const date = st.start_at.split('T')[0];
      if (!calendarMap[date]) calendarMap[date] = {};
      
      const movieData = movieDetails[st.movie_id];
      // The MOVIE RECORD's title is primary (Josh 2026-07-13) — it's the TMDB-recognized
      // name (kept fresh by the server-side title refresh), while venue billing titles
      // carry fest qualifiers/years/foreign spellings. Billing is only the fallback
      // when no record exists.
      const rawTitle = movieData?.title || st.cinema_movie_title;
      const movieTitle = formatMovieTitle(rawTitle || st.movie?.title || "(Untitled)", st.theaterId);
      
      // Skip the "released within the last year" filter for outdoor screenings —
      // NYC Parks and Rooftop Cinema Club mostly program current releases
      // (Zootopia 2, Jurassic World Rebirth, etc.), and the outdoor button is
      // its own opt-in surface so we don't pollute the indoor calendar.
      if (!st.is_outdoor) {
        const oldestReleaseDate = getOldestReleaseDate(movieData?.release_dates);
        if (oldestReleaseDate) {
          const releaseDate = new Date(oldestReleaseDate);
          if (releaseDate >= lastYearStart) {
            return;
          }
        }
      }

      if (!calendarMap[date][st.movie_id]) {
        calendarMap[date][st.movie_id] = {
          movie_id: st.movie_id,
          title: movieTitle,
          // Admin-set custom display title from moderation_info — used as a
          // secondary match key in the calendar search so users can find a
          // row by its custom title even when cinema_movie_title disagrees.
          customTitle: movieData?.moderation_info?.customTitle || null,
          // Smart-search metadata (director/genres/cast ride along with the record)
          director: movieData?.director || null,
          genres: movieData?.genres || null,
          cast_list: movieData?.cast_list || null,
          isShort: movieData?.moderation_info?.isShort || false,
          isProgram: movieData?.moderation_info?.isProgram || false,
          programKind: movieData?.moderation_info?.programKind || null,
          release_dates: movieData?.release_dates || [],
          release_year: movieData?.release_year || null,
          poster_path: movieData?.poster_path || null,
          slug: movieData?.slug || null,
          verified_at: movieData?.verified_at || null,
          showtimes: []
        };
      }
      
      calendarMap[date][st.movie_id].showtimes.push({
        start_at: st.start_at,
        theater: st.theater,
        theaterId: st.theaterId,
        booking_link: st.booking_link,
        cinema_movie_title: st.cinema_movie_title,
        film_format: st.film_format,
        series: st.series,
        is_outdoor: st.is_outdoor || false,
        outdoor_venue: st.outdoor_venue || null,
      });
    });

    // Clean up empty dates/movies and past showtimes
    Object.keys(calendarMap).forEach(date => {
      Object.keys(calendarMap[date]).forEach(movieId => {
        // Filter out past showtimes for today
        if (date === now.toISOString().split('T')[0]) {
          calendarMap[date][movieId].showtimes = calendarMap[date][movieId].showtimes
            .filter(st => new Date(st.start_at) > now);
        }
        
        // Remove movies with no remaining showtimes
        if (calendarMap[date][movieId].showtimes.length === 0) {
          delete calendarMap[date][movieId];
        }
      });
      
      // Remove dates with no movies
      if (Object.keys(calendarMap[date]).length === 0) {
        delete calendarMap[date];
      }
    });

    return calendarMap;
  };

  // Toggle a theater in selectedTheaters (using cinema id).
  const handleCheckboxChange = (cinemaId) => {
    if (selectedTheaters.includes(cinemaId)) {
      setSelectedTheaters(selectedTheaters.filter(id => id !== cinemaId));
    } else {
      setSelectedTheaters([...selectedTheaters, cinemaId]);
    }
  };

  // Toggle all theaters in a chain at once.
  const handleChainToggle = (chainIds) => {
    const allSelected = chainIds.every(id => selectedTheaters.includes(id));
    if (allSelected) {
      setSelectedTheaters(selectedTheaters.filter(id => !chainIds.includes(id)));
    } else {
      const newSelections = chainIds.filter(id => !selectedTheaters.includes(id));
      setSelectedTheaters([...selectedTheaters, ...newSelections]);
    }
  };

  // ===============================
  //  Month Navigation (Arrow Buttons)
  // ===============================
  const goToNextMonth = () => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(next);
  };

  const goToPreviousMonth = () => {
    const realCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const prev = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    if (prev < realCurrentMonth) return;
    setCurrentMonth(prev);
  };

  // =========================
  //   Build the Calendar UI (Calendar View)
  // =========================
  const displayMonth = currentMonth;
  const year = displayMonth.getFullYear();
  const monthIndex = displayMonth.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  const firstWeekday = firstDay.getDay();
  const lastWeekday = lastDay.getDay();
  const daysInMonth = lastDay.getDate();

  const calendarCells = [];
  for (let i = 0; i < firstWeekday; i++) {
    calendarCells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const moviesObj = calendarData[dateStr] || {};

    // Filter movie entries based on selected theaters AND search term
    const movieEntries = Object.values(moviesObj)
      .filter(entry => {
      const matchesTheaters = selectedTheaters.length === 0 || 
        entry.showtimes.some(st => selectedTheaters.includes(st.theaterId));
        const matchesLocations = selectedLocations.length === 0 ||
          entry.showtimes.some(st => THEATER_LOCATIONS[st.theaterId] && 
            selectedLocations.includes(THEATER_LOCATIONS[st.theaterId]));
      const matchesSearch = entryMatchesSearch(entry, searchTerm);
        const matchesReleaseDate = showNoReleaseDateMovies ? 
          !entry.release_dates || Object.keys(entry.release_dates).length === 0 :
          true;
        const matchesFilmFormat = filmFormatFilter === 'all' ||
          (filmFormatFilter === 'film' && entry.showtimes.some(st => st.film_format === '35mm' || st.film_format === '70mm')) ||
          entry.showtimes.some(st => st.film_format === filmFormatFilter);
        const matchesSeries = selectedSeries.length === 0 ||
          entry.showtimes.some(st => selectedSeries.includes(st.series));
        const matchesWatchlist = !onlyWatchlist || isEntryOnWatchlist({ movie_id: entry.movie_id, title: entry.title });
        // Outdoor toggle is opt-in: default shows everything; when ON, restrict
        // to entries that have at least one outdoor showtime.
        const matchesOutdoor = !onlyOutdoor || entry.showtimes.some(st => st.is_outdoor);
        return matchesTheaters && matchesLocations && matchesSearch && matchesReleaseDate && matchesFilmFormat && matchesSeries && matchesWatchlist && matchesOutdoor;
      })
      .sort((a, b) => a.title.localeCompare(b.title));
    const titles = movieEntries.map(m => m.title);
    calendarCells.push({ dateStr, day, titles, movieEntries });
  }
  for (let i = 0; i < (6 - lastWeekday); i++) {
    calendarCells.push(null);
  }

  // =========================
  //   Build the List View UI
  // =========================

  const goToNextWeek = () => {
    const next = new Date(currentWeek);
    next.setDate(next.getDate() + 7);
    setCurrentWeek(next);
  };

  const goToPreviousWeek = () => {
    const prev = new Date(currentWeek);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeek(prev);
  };

  // Day navigation for List View
  const goToNextDay = () => {
    const nextStart = new Date(customStartDate);
    nextStart.setDate(nextStart.getDate() + 1);
    const nextEnd = new Date(customEndDate);
    nextEnd.setDate(nextEnd.getDate() + 1);
    // Update both in a single setSearchParams call to avoid React batching overwrite
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('start', formatDateForURL(nextStart));
      newParams.set('end', formatDateForURL(nextEnd));
      return newParams;
    });
  };

  const goToPreviousDay = () => {
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const prevStart = new Date(customStartDate);
    prevStart.setDate(prevStart.getDate() - 1);
    if (prevStart < todayMidnight) return; // Don't go before today
    const prevEnd = new Date(customEndDate);
    prevEnd.setDate(prevEnd.getDate() - 1);
    // Update both in a single setSearchParams call to avoid React batching overwrite
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('start', formatDateForURL(prevStart));
      newParams.set('end', formatDateForURL(prevEnd));
      return newParams;
    });
  };

  const getWeekDisplay = () => {
    const weekStart = new Date(currentWeek);
    // Adjust to start from Monday
    weekStart.setDate(weekStart.getDate() - (weekStart.getDay() + 6) % 7);

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6); // Go 6 days forward to get to Sunday
    
    const formatDate = (date) => {
      return `${date.toLocaleString('default', { month: 'long' })} ${date.getDate()}`;
    };
    
    if (weekStart.getMonth() === weekEnd.getMonth()) {
      return `${formatDate(weekStart)} - ${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
    } else {
      return `${formatDate(weekStart)} - ${formatDate(weekEnd)}, ${weekStart.getFullYear()}`;
    }
  };

  // Update the renderMovieTitle function to handle years consistently
  const renderMovieTitle = (entry, dateStr) => {
    const reviewStatus = reviewedMovies[entry.movie_id];

    // Shared title+year — identical across month/week/list (see displayTitleYear).
    const { cleanTitle, releaseYear, displayTitle } = displayTitleYear(entry, reviewStatus);

    // Check if any showtime has film format
    const hasFilmFormat = entry.showtimes?.some(st => st.film_format === '35mm' || st.film_format === '70mm');

    if (isExclusionActive(reviewStatus)) {
      return null; // Don't show excluded movies to regular users (auto-expires after 1 year)
    }

    return (
      <li key={`${dateStr}-${entry.movie_id}`}
          className="movie-entry">
        {/* Inline wrapper so the heart attaches to the last word of the
            title even when the title wraps. Paired CSS in App.css overrides
            the .day-movies a {display:block} rule for this <a> only. */}
        <span className="title-heart-wrap">
        <MovieLink
          movieId={entry.movie_id}
          slug={entry.slug || slugify(cleanTitle, releaseYear)}
          state={{
            movieTitle: cleanTitle,
            showtimes: Object.entries(calendarData)
              .flatMap(([dateStr, movies]) => {
                const movieData = movies[entry.movie_id];
                return movieData ? movieData.showtimes : [];
              })
              .sort((a, b) => new Date(a.start_at) - new Date(b.start_at)),
            release_dates: entry.release_dates
          }}
        >
          {displayTitle} {renderBadges(hasFilmFormat, entry.isShort, entry.isProgram, entry.programKind)}
        </MovieLink>
        <WatchlistHeart movieTitle={cleanTitle} movieId={entry.movie_id} size={11} />
        </span>
        {SERIES_UI_ENABLED && entry.showtimes[0]?.series && (
          <span className="series-tag-small">{entry.showtimes[0].series}</span>
        )}

      </li>
    );
  };

  // Add this effect for the location filter
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setIsLocationDropdownOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // Add the useEffect for handling clicks outside the theater dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (theaterDropdownRef.current && !theaterDropdownRef.current.contains(event.target)) {
        setIsTheaterDropdownOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Add the useEffect for handling clicks outside the film dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filmDropdownRef.current && !filmDropdownRef.current.contains(event.target)) {
        setIsFilmDropdownOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close series dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (seriesDropdownRef.current && !seriesDropdownRef.current.contains(event.target)) {
        setIsSeriesDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close My Filters dropdown on outside click — matches the behaviour of
  // the other filter buttons. Without this you have to click the My Filters
  // button a second time to close it.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersDropdownRef.current && !filtersDropdownRef.current.contains(event.target)) {
        setShowFiltersDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Add a function to check if there are any movies in the current view
  const hasMoviesInView = () => {
    if (viewMode === "calendar") {
      return calendarCells.some(cell => cell && cell.movieEntries && cell.movieEntries.length > 0);
    } else if (viewMode === "week") {
      const weekDates = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(currentWeek);
        date.setDate(date.getDate() - (date.getDay() + 6) % 7 + i);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      });
      return weekDates.some(dateStr => {
        const moviesForDate = calendarData[dateStr] || {};
        return Object.values(moviesForDate).length > 0;
      });
    }
    return true;
  };

  // Custom date range from URL
  const todayForCustomDates = new Date();
  todayForCustomDates.setHours(0, 0, 0, 0);
  const customStartDate = parseDateFromURL('start', todayForCustomDates);
  const setCustomStartDate = (date) => setURLParam('start', formatDateForURL(date));
  const customEndDate = parseDateFromURL('end', todayForCustomDates);
  const setCustomEndDate = (date) => setURLParam('end', formatDateForURL(date));
  
  // Custom date mode - true if start/end dates are set in URL
  const isCustomDateMode = getURLParam('start', null) !== null || getURLParam('end', null) !== null;
  const setIsCustomDateMode = (mode) => {
    if (!mode) {
      // Clear date params when disabling custom date mode
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('start');
        newParams.delete('end');
        return newParams;
      });
    }
  };

  // Add these functions near the other toggle functions
  const toggleTheaterFilter = () => {
    setIsTheaterDropdownOpen(!isTheaterDropdownOpen);
    setIsLocationDropdownOpen(false); // Close other dropdown
  };

  const toggleLocationFilter = () => {
    setIsLocationDropdownOpen(!isLocationDropdownOpen);
    setIsTheaterDropdownOpen(false); // Close other dropdown
  };

  // Generate Event Schema for List View (when viewing single day/today)
  const generateEventSchemas = () => {
    if (viewMode !== 'list') return [];
    
    const schemas = [];
    const isViewingSingleDay = isCustomDateMode && 
      customStartDate.toISOString().split('T')[0] === customEndDate.toISOString().split('T')[0];
    
    if (!isViewingSingleDay) return [];

    const dateStr = customStartDate.toISOString().split('T')[0];
    const moviesForDate = calendarData[dateStr] || {};
    
    Object.values(moviesForDate).forEach(movie => {
      movie.showtimes.forEach(showtime => {
        const theater = THEATERS.find(t => t.id === showtime.theaterId);
        schemas.push({
          "@context": "https://schema.org",
          "@type": "ScreeningEvent",
          "name": movie.title,
          "startDate": showtime.start_at,
          "location": {
            "@type": "MovieTheater",
            "name": theater ? theater.name : showtime.theater
          },
          ...(getTicketLink(showtime.booking_link, showtime.theaterId) && {
            "offers": {
              "@type": "Offer",
              "url": getTicketLink(showtime.booking_link, showtime.theaterId),
              "availability": "https://schema.org/InStock"
            }
          })
        });
      });
    });
    
    return schemas;
  };

  const eventSchemas = generateEventSchemas();

  // Filter controls live in ONE place and render in TWO (9/2): the header row
  // for month/week, a left sidebar for the list view — Revival Hub's layout,
  // where the options stay visible beside the listings instead of stacking
  // above them.
  const filterControls = (
<div className="right-controls">
          <DebouncedSearchInput
            initialValue={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search films, directors, actors, theaters..."
            className="search-input"
          />
          {/* Outdoor sits with the other filters (Josh 9/2) — it IS one:
              it filters the current view rather than going anywhere. */}
          {hasUpcomingOutdoorScreenings && (
            <button
              type="button"
              className={`outdoor-button${onlyOutdoor ? ' outdoor-button-active' : ''}`}
              onClick={() => setOnlyOutdoor(!onlyOutdoor)}
              title={onlyOutdoor ? 'Show all screenings' : 'Celebrate summer — see only outdoor screenings'}
            >
              {onlyOutdoor ? '✓ Outdoor Movies' : 'Outdoor Movies!'}
            </button>
          )}
          <div className="film-filter" ref={filmDropdownRef}>
            <button className="filter-button" onClick={() => setIsFilmDropdownOpen(!isFilmDropdownOpen)}>
              Filter by Celluloid
            </button>
            {isFilmDropdownOpen && (
              <div className="filter-dropdown">
                <label className="dropdown-item">
                  <input 
                    type="radio" 
                    checked={filmFormatFilter === 'all'} 
                    onChange={() => setFilmFormatFilter('all')} 
                  />
                  All Movies
                </label>
                <label className="dropdown-item">
                  <input 
                    type="radio" 
                    checked={filmFormatFilter === 'film'} 
                    onChange={() => setFilmFormatFilter('film')} 
                  />
                  Film (35mm or 70mm)
                </label>
                <label className="dropdown-item">
                  <input 
                    type="radio" 
                    checked={filmFormatFilter === '35mm'} 
                    onChange={() => setFilmFormatFilter('35mm')} 
                  />
                  35mm Only
                </label>
                <label className="dropdown-item">
                  <input
                    type="radio"
                    checked={filmFormatFilter === '70mm'}
                    onChange={() => setFilmFormatFilter('70mm')}
                  />
                  70mm Only
                </label>
                <Link
                  to="/35mm-nyc"
                  className="dropdown-item"
                  style={{ color: '#8b6914', fontWeight: 600, borderTop: '1px solid #ede8e0', textDecoration: 'none', display: 'block' }}
                >
                  Browse all 35mm and 70mm →
                </Link>
              </div>
            )}
          </div>
          {availableSeries.length > 0 && (
            <div className="series-filter" ref={seriesDropdownRef}>
              <button className="filter-button" onClick={() => setIsSeriesDropdownOpen(!isSeriesDropdownOpen)}>
                {selectedSeries.length > 0 ? `Series (${selectedSeries.length})` : 'Filter by Series'}
              </button>
              {isSeriesDropdownOpen && (
                <div className="filter-dropdown">
                  {selectedSeries.length > 0 && (
                    <label className="dropdown-item" style={{ color: '#8b6914', fontWeight: 600, cursor: 'pointer' }}
                      onClick={() => setSelectedSeries([])}>
                      Clear All
                    </label>
                  )}
                  {availableSeries.map(series => (
                    <label key={series} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={selectedSeries.includes(series)}
                        onChange={() => {
                          if (selectedSeries.includes(series)) {
                            setSelectedSeries(selectedSeries.filter(s => s !== series));
                          } else {
                            setSelectedSeries([...selectedSeries, series]);
                          }
                        }}
                      />
                      {series}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="theater-filter" ref={theaterDropdownRef}>
            <button className="filter-button" onClick={toggleTheaterFilter}>
              Filter by Theater
            </button>
            {isTheaterDropdownOpen && (() => {
              const chainedIds = new Set(THEATER_CHAINS.flatMap(c => c.ids));
              const outdoorIdSet = new Set(OUTDOOR_THEATER_IDS);
              const standaloneTheaters = THEATERS.filter(t => !chainedIds.has(t.id) && !outdoorIdSet.has(t.id));
              const outdoorTheaters = THEATERS.filter(t => outdoorIdSet.has(t.id));
              const independentIds = standaloneTheaters.map(t => t.id);
              const outdoorIds = outdoorTheaters.map(t => t.id);
              const independentSelected = independentIds.filter(id => selectedTheaters.includes(id)).length;
              const outdoorSelected = outdoorIds.filter(id => selectedTheaters.includes(id)).length;
              const independentAllSelected = independentSelected === independentIds.length && independentIds.length > 0;
              const independentSomeSelected = independentSelected > 0 && !independentAllSelected;
              const outdoorAllSelected = outdoorSelected === outdoorIds.length && outdoorIds.length > 0;
              const outdoorSomeSelected = outdoorSelected > 0 && !outdoorAllSelected;
              return (
                <div className="filter-dropdown">
                  {THEATER_CHAINS.map(chain => {
                    const chainTheaters = chain.ids
                      .map(id => THEATERS.find(t => t.id === id))
                      .filter(Boolean);
                    const selectedCount = chain.ids.filter(id => selectedTheaters.includes(id)).length;
                    const allSelected = selectedCount === chain.ids.length;
                    const someSelected = selectedCount > 0 && !allSelected;
                    return (
                      <div key={chain.name} className="chain-group">
                        <label className="chain-header">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={el => { if (el) el.indeterminate = someSelected; }}
                            onChange={() => handleChainToggle(chain.ids)}
                          />
                          <span className="chain-name">{chain.name}</span>
                          {selectedCount > 0 && (
                            <span className="chain-count">({selectedCount}/{chain.ids.length})</span>
                          )}
                        </label>
                        <div className="chain-members">
                          {chainTheaters.map(theater => (
                            <label key={theater.id} className="dropdown-item">
                              <input
                                type="checkbox"
                                checked={selectedTheaters.includes(theater.id)}
                                onChange={() => handleCheckboxChange(theater.id)}
                              />
                              {theater.name
                                .replace(/^Alamo Drafthouse\s*/i, '')
                                .replace(/^AMC (DINE-IN\s*)?/i, '')
                                .replace(/^Angelika Film Center & Café/i, 'Film Center & Café')
                                .replace(/^Cinema 123 by Angelika/i, 'Cinema 123')
                                .replace(/^Village East by Angelika/i, 'Village East')
                                .replace(/^Nitehawk\s*/i, '')
                                .replace(/^Regal\s*(UA\s*)?/i, '')}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <div className="standalone-section">
                    <label className="chain-header">
                      <input
                        type="checkbox"
                        checked={independentAllSelected}
                        ref={el => { if (el) el.indeterminate = independentSomeSelected; }}
                        onChange={() => handleChainToggle(independentIds)}
                      />
                      <span className="chain-name">Independent Cinemas</span>
                      {independentSelected > 0 && (
                        <span className="chain-count">({independentSelected}/{independentIds.length})</span>
                      )}
                    </label>
                    <div className="chain-members">
                      {standaloneTheaters.map(theater => (
                        <label key={theater.id} className="dropdown-item">
                          <input
                            type="checkbox"
                            checked={selectedTheaters.includes(theater.id)}
                            onChange={() => handleCheckboxChange(theater.id)}
                          />
                          {theater.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  {outdoorTheaters.length > 0 && (
                    <div className="standalone-section">
                      <label className="chain-header">
                        <input
                          type="checkbox"
                          checked={outdoorAllSelected}
                          ref={el => { if (el) el.indeterminate = outdoorSomeSelected; }}
                          onChange={() => handleChainToggle(outdoorIds)}
                        />
                        <span className="chain-name">Outdoor Movies</span>
                        {outdoorSelected > 0 && (
                          <span className="chain-count">({outdoorSelected}/{outdoorIds.length})</span>
                        )}
                      </label>
                      <div className="chain-members">
                        {outdoorTheaters.map(theater => (
                          <label key={theater.id} className="dropdown-item">
                            <input
                              type="checkbox"
                              checked={selectedTheaters.includes(theater.id)}
                              onChange={() => handleCheckboxChange(theater.id)}
                            />
                            {theater.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="location-filter" ref={locationDropdownRef}>
            <button className="filter-button" onClick={toggleLocationFilter}>
              Filter by Location
            </button>
            {isLocationDropdownOpen && (
              <div className="filter-dropdown">
                {LOCATIONS.map(location => (
                  <label key={location} className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={selectedLocations.includes(location)}
                      onChange={() => {
                        if (selectedLocations.includes(location)) {
                          setSelectedLocations(selectedLocations.filter(l => l !== location));
                        } else {
                          setSelectedLocations([...selectedLocations, location]);
                        }
                      }}
                    />
                    {location}
                  </label>
                ))}
              </div>
            )}
          </div>
          {user && (
            <div className="my-filters" ref={filtersDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button className="filter-button" onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}>
                My Filters
              </button>
              {showFiltersDropdown && (
                <div className="filter-dropdown" style={{ minWidth: '200px', right: 0 }}>
                  {watchlist && watchlist.length > 0 && (
                    <div style={{ padding: '6px 8px', borderBottom: '1px solid #d4cec4', marginBottom: '4px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.85rem', color: '#2a2520' }}>
                        <input
                          type="checkbox"
                          checked={onlyWatchlist}
                          onChange={() => setOnlyWatchlist(!onlyWatchlist)}
                          style={{ marginRight: '6px', cursor: 'pointer' }}
                        />
                        ♥ Only show my watchlist ({watchlist.length})
                      </label>
                    </div>
                  )}
                  {savedFilters.length > 0 && savedFilters.map(f => (
                    <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px' }}>
                      <button
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (f.theaters) params.set('theaters', f.theaters);
                          if (f.locations) params.set('locations', f.locations);
                          if (f.format) params.set('format', f.format);
                          if (f.search) params.set('search', f.search);
                          setSearchParams(params);
                          setShowFiltersDropdown(false);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2a2520', fontSize: '0.85rem', textAlign: 'left', flex: 1 }}
                      >
                        {f.filter_name}
                      </button>
                      <button
                        onClick={() => deleteFilter(f.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cc4444', fontSize: '0.75rem', padding: '2px 4px' }}
                      >
                        x
                      </button>
                    </div>
                  ))}
                  {savedFilters.length === 0 && <p style={{ padding: '8px', color: '#6b6255', fontSize: '0.8rem', margin: 0 }}>No saved filters yet</p>}
                  <div style={{ borderTop: '1px solid #d4cec4', padding: '8px' }}>
                    {showSaveFilterModal ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <input
                          type="text"
                          value={filterName}
                          onChange={e => setFilterName(e.target.value)}
                          placeholder="Filter name"
                          style={{ flex: 1, padding: '4px 8px', border: '1px solid #d4cec4', borderRadius: '3px', fontSize: '0.8rem' }}
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (!filterName.trim()) return;
                            saveFilter(filterName.trim(), {
                              theaters: searchParams.get('theaters') || null,
                              locations: searchParams.get('locations') || null,
                              format: searchParams.get('format') || null,
                              search: searchParams.get('search') || null,
                            });
                            setFilterName('');
                            setShowSaveFilterModal(false);
                          }}
                          style={{ padding: '4px 10px', background: '#8b6914', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.8rem' }}
                        >
                          Save
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowSaveFilterModal(true)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b6914', fontSize: '0.8rem', padding: 0 }}
                      >
                        + Save current filters
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
  );

  // filterControls MUST be declared before listHeader: listHeader's JSX is
  // evaluated at assignment and contains {viewMode !== 'list' && filterControls}.
  // In list view that short-circuits, but in month/week it read filterControls
  // before initialisation — a TDZ ReferenceError that blanked the whole app.
  // (Live outage 9/2-9/4, introduced when these two were extracted.)
  const listHeader = (
<div className="calendar-header">
        <div className="left-controls">
          {/* Filter by Watchlist UI is intentionally not rendered here yet —
              the state + filter logic stay in place so we can wire this into
              the My Filters dropdown later. */}
        </div>

        <div className="middle-controls">
          {viewMode !== "list" ? (
            <>
          <button 
            className="nav-button" 
                onClick={viewMode === "week" ? goToPreviousWeek : goToPreviousMonth}
                disabled={viewMode === "week" ? 
                  currentWeek <= today : 
                  currentMonth.getTime() === new Date(today.getFullYear(), today.getMonth(), 1).getTime()}
          >
            &#8592;
          </button>
              <h1>
                {viewMode === "week" ? 
                  getWeekDisplay() : 
                  `${firstDay.toLocaleString('default', { month: 'long' })} ${year}`}
              </h1>
              <button 
                className="nav-button" 
                onClick={viewMode === "week" ? goToNextWeek : goToNextMonth}
              >
            &#8594;
          </button>
            </>
          ) : (
            <>
              {/* Check if viewing a single day to show navigation arrows */}
              {customStartDate.toDateString() === customEndDate.toDateString() ? (
                <>
                  <button 
                    className="nav-button" 
                    onClick={goToPreviousDay}
                    disabled={customStartDate.toISOString().split('T')[0] === new Date().toISOString().split('T')[0]}
                  >
                    &#8592;
                  </button>
                  <h1 style={{ color: '#6b6255' }}>
                    {customStartDate.toLocaleDateString('en-US', { 
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </h1>
                  <button 
                    className="nav-button" 
                    onClick={goToNextDay}
                  >
                    &#8594;
                  </button>
                </>
              ) : (
                <h1 style={{ color: isCustomDateMode ? '#666' : 'inherit' }}>
                  {isCustomDateMode ? (
                    `${customStartDate.toLocaleDateString('en-US', { 
                      month: 'long',
                      day: 'numeric'
                    })} - ${customEndDate.toLocaleDateString('en-US', { 
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}`
                  ) : (
                    `${firstDay.toLocaleString('default', { month: 'long' })} ${year}`
                  )}
                </h1>
              )}
            </>
          )}
        </div>

        {viewMode !== 'list' && filterControls}

      </div>
  );


  return (
    <>
      <Helmet>
        <title>{`NYC Revival & Repertory Showtimes — ${firstDay.toLocaleString('default', { month: 'long' })} ${year} | Film Revival NYC`}</title>
        <meta name="description" content={`Every revival, 35mm, and repertory film playing in NYC in ${firstDay.toLocaleString('default', { month: 'long' })} ${year}. Updated daily from Film Forum, Metrograph, BAM, Anthology Film Archives, and 20+ cinemas.`} />
        <meta property="og:title" content="Film Revival NYC — NYC Arthouse & Revival Showtimes" />
        <meta property="og:description" content={`Every revival, 35mm, and repertory film playing in NYC in ${firstDay.toLocaleString('default', { month: 'long' })} ${year}. Updated daily from Film Forum, Metrograph, BAM, Anthology Film Archives, and 20+ cinemas.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://filmrevivalnyc.com/" />
        <meta property="og:image" content="https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Film Revival NYC — NYC Arthouse & Revival Showtimes" />
        <meta name="twitter:description" content={`Every revival, 35mm, and repertory film playing in NYC in ${firstDay.toLocaleString('default', { month: 'long' })} ${year}. Updated daily from Film Forum, Metrograph, BAM, Anthology Film Archives, and 20+ cinemas.`} />
        <meta name="twitter:image" content="https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg" />
        <link rel="canonical" href="https://filmrevivalnyc.com/" />
        {/* Event Schema structured data for List View */}
        {eventSchemas.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      {/* Intro paragraph removed 9/2 (Josh): it duplicated the meta description
          and the new /venues page, and pushed the calendar below the fold. The
          month-aware <meta name="description"> above still carries the same
          venue names for search results. */}

      {/* Welcome newsletter ask: fresh account, not yet subscribed (see the
          state block above for why this exists). One ask, dismiss is final. */}
      {(welcomeAsk === 'show' || welcomeAsk === 'busy' || welcomeAsk === 'done') && (
        <div style={{
          background: '#f5ecd9', border: '1px solid #d4cec4', borderRadius: '6px',
          padding: '12px 14px', margin: '0 auto 16px', maxWidth: '900px',
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          fontSize: '0.9rem', color: '#2a2520',
        }}>
          <span style={{ fontSize: '1.1rem' }}>✉️</span>
          {welcomeAsk === 'done' ? (
            <span style={{ flex: 1, minWidth: '220px' }}>
              <strong>You're on the list!</strong> Your first Revival Rundown arrives Sunday.
            </span>
          ) : (
            <>
              <span style={{ flex: 1, minWidth: '220px' }}>
                <strong>Want the weekly Revival Rundown?</strong> One email every Sunday with the week's best revival screenings.
              </span>
              <button onClick={() => answerWelcomeAsk(true)} disabled={welcomeAsk === 'busy'} style={{
                background: '#8b6914', color: '#fff', border: 'none', borderRadius: '6px',
                padding: '7px 14px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                opacity: welcomeAsk === 'busy' ? 0.6 : 1,
              }}>
                {welcomeAsk === 'busy' ? 'Adding…' : 'Add me'}
              </button>
              <button onClick={() => answerWelcomeAsk(false)} style={{
                background: 'none', border: '1px solid #d4cec4', color: '#6b6255', borderRadius: '6px',
                padding: '7px 14px', fontSize: '0.85rem', cursor: 'pointer',
              }}>
                No thanks
              </button>
            </>
          )}
        </div>
      )}

      {/* Header: Grid with 3 columns (left: view toggle, middle: month nav, right: filter) */}
      {/* VIEW CONTROLS (9/2, Josh): Home holds the list view, so the only
          switch the grid needs is Week vs Month. List mode keeps just its
          Compact toggle — nothing else to choose there. */}
      {viewMode !== 'list' && (
        <div className="view-switcher">
          {[
            { key: 'week', label: 'Week' },
            { key: 'calendar', label: 'Month' },
          ].map((v) => (
            <button
              key={v.key}
              className={`view-switch-btn${viewMode === v.key ? ' is-active' : ''}`}
              aria-pressed={viewMode === v.key}
              onClick={() => setViewMode(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}

      {viewMode !== 'list' ? listHeader : <div className="list-header-desktop">{listHeader}</div>}

      {viewMode === "calendar" ? (
        <>
          <div className="calendar-grid">
            {/* Header cells */}
            <div className="header-cell">Sun</div>
            <div className="header-cell">Mon</div>
            <div className="header-cell">Tue</div>
            <div className="header-cell">Wed</div>
            <div className="header-cell">Thu</div>
            <div className="header-cell">Fri</div>
            <div className="header-cell">Sat</div>
            
            {/* Calendar cells */}
            {calendarCells.map((cell, idx) => {
              if (cell === null) {
                return <div className="day-cell empty" key={`empty-${idx}`}></div>;
              }
              const { dateStr, day, movieEntries } = cell;
              return (
                <div className="day-cell" key={dateStr}>
                  <div className="date-number">{day}</div>
                  <div className="titles-container">
                    <ul className="titles-list">
                      {movieEntries.map((entry, i) => (
                        renderMovieTitle(entry, dateStr)
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : viewMode === "week" ? (
        <div className="weekly-view">
          <div className="weekly-grid">
            {/* Header row with day names and dates */}
            <div className="weekly-header">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayName, index) => {
                // Calculate the date for this column (same logic as content columns)
                const date = new Date(currentWeek);
                date.setDate(date.getDate() - (date.getDay() + 6) % 7 + index);
                const dayNumber = date.getDate();
                const monthNumber = date.getMonth() + 1;
                
                return (
                  <div key={dayName} className="header-cell">
                    {dayName}
                    <br />
                    <span style={{ fontSize: '0.8em', color: '#6b6255' }}>
                      {monthNumber}/{dayNumber}
                    </span>
                  </div>
                );
              })}
            </div>
            
            {/* Content columns */}
            <div className="weekly-content">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                // Calculate the date for this column
                const date = new Date(currentWeek);
                date.setDate(date.getDate() - (date.getDay() + 6) % 7 + index);
                const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                
                // Group movies by theater for this day
                const theaterMovies = {};
                const moviesObjForDay = calendarData[dateStr] || {};
                
                Object.values(moviesObjForDay).forEach(movie => {
                  // Apply filters
                  const matchesSearch = entryMatchesSearch(movie, searchTerm);
                  const matchesReleaseDate = showNoReleaseDateMovies ?
                    !movie.release_dates || Object.keys(movie.release_dates).length === 0 :
                    true;
                  const reviewStatus = reviewedMovies[movie.movie_id];
                  const isExcluded = isExclusionActive(reviewStatus);
                  const matchesWatchlist = !onlyWatchlist || isEntryOnWatchlist({ movie_id: movie.movie_id, title: movie.title });
                  const matchesOutdoor = !onlyOutdoor || movie.showtimes.some(st => st.is_outdoor);

                  if (!matchesSearch || !matchesReleaseDate || isExcluded || !matchesWatchlist || !matchesOutdoor) {
                    return;
                  }

                  // Group showtimes by theater
                  movie.showtimes.forEach(showtime => {
                    const theaterId = showtime.theaterId;
                    // When the outdoor filter is on, strip individual indoor
                    // showtimes from a mixed entry so the calendar only
                    // surfaces the outdoor ones.
                    if (onlyOutdoor && !showtime.is_outdoor) return;

                    // Apply theater and location filters
                    const matchesTheater = selectedTheaters.length === 0 || selectedTheaters.includes(theaterId);
                    const matchesLocation = selectedLocations.length === 0 ||
                      (THEATER_LOCATIONS[theaterId] && selectedLocations.includes(THEATER_LOCATIONS[theaterId]));

                    // Apply film format filter
                    const matchesFilmFormat = filmFormatFilter === 'all' ||
                      (filmFormatFilter === 'film' && (showtime.film_format === '35mm' || showtime.film_format === '70mm')) ||
                      showtime.film_format === filmFormatFilter;

                    const matchesSeries = selectedSeries.length === 0 ||
                      selectedSeries.includes(showtime.series);

                    if (!matchesTheater || !matchesLocation || !matchesFilmFormat || !matchesSeries) {
                      return;
                    }

                    if (!theaterMovies[theaterId]) {
                      const theater = THEATERS.find(t => t.id === theaterId);
                      theaterMovies[theaterId] = {
                        theaterName: theater ? theater.name : 'Unknown Theater',
                        movies: {}
                      };
                    }
                    
                    if (!theaterMovies[theaterId].movies[movie.movie_id]) {
                      theaterMovies[theaterId].movies[movie.movie_id] = {
                        ...movie,
                        showtimesForThisTheater: []
                      };
                    }
                    
                    theaterMovies[theaterId].movies[movie.movie_id].showtimesForThisTheater.push(showtime);
                  });
                });

            return (
                  <div key={day} className="day-column">
                    <div className="day-content">
                    {(() => {
                        const AMC_IDS = new Set(THEATER_CHAINS.find(c => c.name === 'AMC')?.ids || []);
                        const REGAL_IDS = new Set(THEATER_CHAINS.find(c => c.name === 'Regal')?.ids || []);
                        const standaloneWkEntries = Object.entries(theaterMovies)
                          .filter(([id]) => !AMC_IDS.has(id) && !REGAL_IDS.has(id))
                          .sort(([, a], [, b]) => a.theaterName.localeCompare(b.theaterName));
                        const amcWkEntries = Object.entries(theaterMovies)
                          .filter(([id]) => AMC_IDS.has(id))
                          .sort(([, a], [, b]) => a.theaterName.localeCompare(b.theaterName));
                        const regalWkEntries = Object.entries(theaterMovies)
                          .filter(([id]) => REGAL_IDS.has(id))
                          .sort(([, a], [, b]) => a.theaterName.localeCompare(b.theaterName));

                        const renderWkMovies = (theaterId, theaterData) =>
                          Object.values(theaterData.movies)
                            .sort((a, b) => a.title.localeCompare(b.title))
                            .map(movie => {
                              const reviewStatus = reviewedMovies[movie.movie_id];
                              // Shared title+year — identical across month/week/list.
                              const { cleanTitle, releaseYear } = displayTitleYear(movie, reviewStatus);
                              const hasFilmFormat = movie.showtimesForThisTheater.some(st => st.film_format === '35mm' || st.film_format === '70mm');
                              return (
                                <div key={movie.movie_id} style={{ marginBottom: '12px' }}>
                                  <div style={{ marginBottom: '4px' }}>
                                    <MovieLink
                                      movieId={movie.movie_id}
                                      slug={movie.slug || slugify(cleanTitle, releaseYear)}
                                      state={{
                                        movieTitle: cleanTitle,
                                        showtimes: Object.entries(calendarData)
                                          .flatMap(([dateStr, movies]) => {
                                            const movieData = movies[movie.movie_id];
                                            return movieData ? movieData.showtimes : [];
                                          })
                                          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at)),
                                        release_dates: movie.release_dates
                                      }}
                                    >
                                      <span style={{ fontSize: '0.85rem' }}>
                                        {cleanTitle}{releaseYear ? ` (${releaseYear})` : ''} {renderBadges(hasFilmFormat, movie.isShort, movie.isProgram, movie.programKind)}
                                      </span>
                                    </MovieLink>
                                    <WatchlistHeart movieTitle={cleanTitle} movieId={movie.movie_id} size={14} />
                                    {SERIES_UI_ENABLED && movie.showtimesForThisTheater[0]?.series && (
                                      <span className="series-tag-small">{movie.showtimesForThisTheater[0].series}</span>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '0.75rem' }}>
                                    {movie.showtimesForThisTheater
                                      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
                                      .map((showtime, index) => {
                                        const time = new Date(showtime.start_at).toLocaleTimeString('en-US', {
                                          hour: 'numeric',
                                          minute: '2-digit'
                                        });
                                        return (
                                          <span key={index} className="showtime-badge" style={{
                                            padding: '2px 6px',
                                            backgroundColor: '#f0f0f0',
                                            borderRadius: '3px',
                                            fontSize: '0.75rem'
                                          }}>
                                            {time}
                                          </span>
                                        );
                                      })}
                                  </div>
                                </div>
                              );
                            });

                        const wkH3Style = { fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '8px', color: '#1a1714', borderBottom: '1px solid #2a2520', paddingBottom: '4px' };
                        const allWkItems = [
                          ...standaloneWkEntries.map(([theaterId, theaterData]) => ({
                            sortKey: theaterData.theaterName,
                            render: () => (
                              <div key={theaterId} className="week-theater-group" style={{ marginBottom: '15px' }}>
                                <h3 style={wkH3Style}>{theaterData.theaterName}</h3>
                                {renderWkMovies(theaterId, theaterData)}
                              </div>
                            )
                          })),
                          ...(amcWkEntries.length > 0 ? [{
                            sortKey: 'AMC',
                            render: () => {
                              const amcWkMerged = {};
                              amcWkEntries.forEach(([tId, tData]) => {
                                Object.values(tData.movies).forEach(movie => {
                                  if (!amcWkMerged[movie.movie_id]) {
                                    amcWkMerged[movie.movie_id] = { ...movie, showtimesForThisTheater: [], primaryTheaterId: tId };
                                  }
                                  amcWkMerged[movie.movie_id].showtimesForThisTheater.push(...movie.showtimesForThisTheater);
                                });
                              });
                              return (
                                <div key="amc-chain-wk" className="week-theater-group" style={{ marginBottom: '15px' }}>
                                  <h3 style={wkH3Style}>AMC</h3>
                                  <p style={{ fontSize: '0.7rem', color: '#aaa', fontStyle: 'italic', margin: '0 0 4px 0' }}>
                                    Click title to see participating theaters
                                  </p>
                                  {Object.values(amcWkMerged)
                                    .sort((a, b) => a.title.localeCompare(b.title))
                                    .map(movie => renderWkMovies(movie.primaryTheaterId, { movies: { [movie.movie_id]: movie } }))
                                  }
                                </div>
                              );
                            }
                          }] : []),
                          ...(regalWkEntries.length > 0 ? [{
                            sortKey: 'Regal',
                            render: () => {
                              const regalWkMerged = {};
                              regalWkEntries.forEach(([tId, tData]) => {
                                Object.values(tData.movies).forEach(movie => {
                                  if (!regalWkMerged[movie.movie_id]) {
                                    regalWkMerged[movie.movie_id] = { ...movie, showtimesForThisTheater: [], primaryTheaterId: tId };
                                  }
                                  regalWkMerged[movie.movie_id].showtimesForThisTheater.push(...movie.showtimesForThisTheater);
                                });
                              });
                              return (
                                <div key="regal-chain-wk" className="week-theater-group" style={{ marginBottom: '15px' }}>
                                  <h3 style={wkH3Style}>Regal</h3>
                                  <p style={{ fontSize: '0.7rem', color: '#aaa', fontStyle: 'italic', margin: '0 0 4px 0' }}>
                                    Click title to see participating theaters
                                  </p>
                                  {Object.values(regalWkMerged)
                                    .sort((a, b) => a.title.localeCompare(b.title))
                                    .map(movie => renderWkMovies(movie.primaryTheaterId, { movies: { [movie.movie_id]: movie } }))
                                  }
                                </div>
                              );
                            }
                          }] : []),
                        ];
                        return allWkItems
                          .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
                          .map(item => item.render());
                    })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="list-layout">
          {/* Options rail (9/2, Josh — Revival Hub's layout): filters stay
              visible beside the listings instead of stacking above them. On
              narrow screens it returns to a normal stacked row. */}
          <aside className="list-sidebar">
            <h2 className="list-sidebar-title">Options</h2>
            <div className="sidebar-view-switcher">
              <button className={`view-switch-btn${listSort === 'theater' ? ' is-active' : ''}`} onClick={() => setListSort('theater')}>By Theater</button>
              <button className={`view-switch-btn${listSort === 'title' ? ' is-active' : ''}`} onClick={() => setListSort('title')}>By Title</button>
            </div>
            <button
              className={`view-switch-btn sidebar-compact${compactMode ? ' is-active' : ''}`}
              onClick={() => setCompactMode(!compactMode)}
            >
              {compactMode ? 'Show Images' : 'Compact'}
            </button>
            {filterControls}
          </aside>
          <div className="list-view">
          {viewMode === 'list' && <div className="list-header-mobile">{listHeader}</div>}
          <div className="date-selector" style={{ 
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
            paddingLeft: '20px'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: '10px'
            }}>
              <button 
                className="custom-date-button"
                onClick={() => {
                  // Combine all URL updates into a single setSearchParams call
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const todayStr = today.toISOString().split('T')[0];
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('start', todayStr);
                    newParams.set('end', todayStr);
                    return newParams;
                  });
                }}
              >
                Today
              </button>
            </div>
            
            <div className="custom-date-inputs" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '32px'
            }}>
              <input
                type="date"
                value={customStartDate.toISOString().split('T')[0]}
                onChange={(e) => {
                  const [year, month, day] = e.target.value.split('-');
                  const date = new Date(year, month - 1, day);
                  setCustomStartDate(date);
                }}
                style={{
                  height: '100%',
                  padding: '0 8px',
                  boxSizing: 'border-box',
                  border: '1px solid #d4cec4',
                  borderRadius: '6px',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '0.85rem',
                  color: '#2a2520',
                  background: '#fff'
                }}
              />
              <span style={{
                lineHeight: '32px',
                color: '#6b6255',
                fontSize: '0.85rem'
              }}>
                to
              </span>
              <input
                type="date"
                value={customEndDate.toISOString().split('T')[0]}
                onChange={(e) => {
                  const [year, month, day] = e.target.value.split('-');
                  const date = new Date(year, month - 1, day);
                  setCustomEndDate(date);
                }}
                style={{
                  height: '100%',
                  padding: '0 8px',
                  boxSizing: 'border-box',
                  border: '1px solid #d4cec4',
                  borderRadius: '6px',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '0.85rem',
                  color: '#2a2520',
                  background: '#fff'
                }}
              />
            </div>
          </div>
          <div className="movies-list">
            {(() => {
              // Default to today when no URL params; respect URL params when set
              const firstDay = customStartDate;
              const lastDay = customEndDate;

              // Group movies by theater
              const theaterMovies = {};
              
              Object.entries(calendarData)
                .filter(([dateStr]) => {
                  const dateToCompare = dateStr.split('T')[0];
                  const startDate = firstDay.toISOString().split('T')[0];
                  const endDate = lastDay.toISOString().split('T')[0];
                  return dateToCompare >= startDate && dateToCompare <= endDate;
                })
                .forEach(([dateStr, movies]) => {
                  Object.values(movies).forEach(movie => {
                    // Apply filters
                    const matchesSearch = entryMatchesSearch(movie, searchTerm);
                    const matchesReleaseDate = showNoReleaseDateMovies ?
                      !movie.release_dates || Object.keys(movie.release_dates).length === 0 :
                      true;
                    const reviewStatus = reviewedMovies[movie.movie_id];
                    const isExcluded = isExclusionActive(reviewStatus);
                    const matchesWatchlist = !onlyWatchlist || isEntryOnWatchlist({ movie_id: movie.movie_id, title: movie.title });
                    const matchesOutdoor = !onlyOutdoor || movie.showtimes.some(st => st.is_outdoor);

                    if (!matchesSearch || !matchesReleaseDate || isExcluded || !matchesWatchlist || !matchesOutdoor) {
                      return;
                    }

                    // Group showtimes by theater
                    movie.showtimes.forEach(showtime => {
                      const theaterId = showtime.theaterId;
                      // When outdoor filter is on, strip indoor showtimes from
                      // a mixed entry so only the outdoor ones surface.
                      if (onlyOutdoor && !showtime.is_outdoor) return;

                      // Apply theater and location filters
                      const matchesTheater = selectedTheaters.length === 0 || selectedTheaters.includes(theaterId);
                      const matchesLocation = selectedLocations.length === 0 ||
                        (THEATER_LOCATIONS[theaterId] && selectedLocations.includes(THEATER_LOCATIONS[theaterId]));
                      
                      // Apply film format filter
                      const matchesFilmFormat = filmFormatFilter === 'all' ||
                        (filmFormatFilter === 'film' && (showtime.film_format === '35mm' || showtime.film_format === '70mm')) ||
                        showtime.film_format === filmFormatFilter;

                      const matchesSeries = selectedSeries.length === 0 ||
                        selectedSeries.includes(showtime.series);

                      if (!matchesTheater || !matchesLocation || !matchesFilmFormat || !matchesSeries) {
                        return;
                      }
                      
                      if (!theaterMovies[theaterId]) {
                        const theater = THEATERS.find(t => t.id === theaterId);
                        theaterMovies[theaterId] = {
                          theaterName: theater ? theater.name : 'Unknown Theater',
                          movies: {}
                        };
                      }
                      
                      if (!theaterMovies[theaterId].movies[movie.movie_id]) {
                        theaterMovies[theaterId].movies[movie.movie_id] = {
                          ...movie,
                          showtimesForThisTheater: []
                        };
                      }
                      
                      theaterMovies[theaterId].movies[movie.movie_id].showtimesForThisTheater.push(showtime);
                    });
                  });
                });
              
              // Check if viewing "today only"
              const isViewingTodayOnly =
                customStartDate.toDateString() === customEndDate.toDateString();
              
              // Split theaters: standalone vs AMC/Regal chains
              const AMC_IDS_L = new Set(THEATER_CHAINS.find(c => c.name === 'AMC')?.ids || []);
              const REGAL_IDS_L = new Set(THEATER_CHAINS.find(c => c.name === 'Regal')?.ids || []);
              const standaloneListEntries = Object.entries(theaterMovies)
                .filter(([id]) => !AMC_IDS_L.has(id) && !REGAL_IDS_L.has(id))
                .sort(([, a], [, b]) => a.theaterName.localeCompare(b.theaterName));
              const amcListEntries = Object.entries(theaterMovies)
                .filter(([id]) => AMC_IDS_L.has(id))
                .sort(([, a], [, b]) => a.theaterName.localeCompare(b.theaterName));
              const regalListEntries = Object.entries(theaterMovies)
                .filter(([id]) => REGAL_IDS_L.has(id))
                .sort(([, a], [, b]) => a.theaterName.localeCompare(b.theaterName));

              const renderListMovies = (theaterId, theaterData, venueInfo = null) =>
                Object.values(theaterData.movies)
                  .sort((a, b) => a.title.localeCompare(b.title))
                  .map(movie => {
                    const reviewStatus = reviewedMovies[movie.movie_id];
                    if (isExclusionActive(reviewStatus)) return null;
                    // Shared title+year — identical across month/week/list.
                    const { cleanTitle, releaseYear: yearToDisplay } = displayTitleYear(movie, reviewStatus);
                    const firstShowtime = movie.showtimesForThisTheater[0];
                    const theater = THEATERS.find(t => t.id === theaterId);
                    const hasFilmFormat = movie.showtimesForThisTheater.some(st => st.film_format === '35mm' || st.film_format === '70mm');
                    return (
                      <div key={movie.movie_id} className="theater-movie-item">
                        {(posterSrc(movie.poster_path) || moviePosters[movie.movie_id]) ? (
                          <img
                            src={posterSrc(movie.poster_path) || moviePosters[movie.movie_id]}
                            alt={cleanTitle}
                            style={{ width: '100%', height: '160px', display: 'block', objectFit: 'cover' }}
                          />
                        ) : (
                          /* Typographic placeholder for poster-less films (8/14):
                             title + year on the dark Art Deco card instead of
                             repeating the generic site image on every stub. */
                          <div style={{ width: '100%', height: '160px', background: '#1a1714', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px', boxSizing: 'border-box', textAlign: 'center' }}>
                            <span style={{ fontFamily: "'Cinzel', Georgia, serif", color: '#d4a843', fontSize: '0.95rem', lineHeight: 1.3, maxHeight: '110px', overflow: 'hidden' }}>{cleanTitle}</span>
                            {yearToDisplay && <span style={{ color: '#8a8272', fontSize: '0.72rem', marginTop: '7px', letterSpacing: '0.1em' }}>{yearToDisplay}</span>}
                          </div>
                        )}
                        <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <MovieLink
                          movieId={movie.movie_id}
                          slug={movie.slug || slugify(cleanTitle, yearToDisplay)}
                          state={{
                            movieTitle: cleanTitle,
                            showtimes: Object.entries(calendarData)
                              .flatMap(([dateStr, movies]) => {
                                const movieData = movies[movie.movie_id];
                                return movieData ? movieData.showtimes : [];
                              })
                              .sort((a, b) => new Date(a.start_at) - new Date(b.start_at)),
                            release_dates: movie.release_dates
                          }}
                        >
                          {cleanTitle}{yearToDisplay ? ` (${yearToDisplay})` : ''} {renderBadges(hasFilmFormat, movie.isShort, movie.isProgram, movie.programKind)}
                        </MovieLink>
                        <WatchlistHeart movieTitle={cleanTitle} movieId={movie.movie_id} size={16} />
                        {/* By Title (9/2): ONE card per film. A single venue
                            names itself; several collapse to a count with a
                            link to the film page, where every time lives —
                            eight identical Akira cards helped nobody. */}
                        {venueInfo && venueInfo.length === 1 && (
                          <Link to={`/theater/${venueInfo[0].slug || venueInfo[0].id}`} className="card-venue-line">
                            {venueInfo[0].name}
                          </Link>
                        )}
                        {venueInfo && venueInfo.length > 1 && (
                          <Link
                            to={`/films/${movie.slug || slugify(cleanTitle, yearToDisplay)}`}
                            className="card-venue-line"
                          >
                            {venueInfo.length} venues · {venueInfo.reduce((n, v) => n + v.count, 0)} showtimes — see all times →
                          </Link>
                        )}
                        {(SERIES_UI_ENABLED || SERIES_UI_WHITELIST.has(movie.showtimesForThisTheater[0]?.series)) && movie.showtimesForThisTheater[0]?.series && (
                          <span className="series-tag">{movie.showtimesForThisTheater[0].series}</span>
                        )}
                        <div className="showtimes-row">
                          {isViewingTodayOnly && !(venueInfo && venueInfo.length > 1) && !AMC_IDS_L.has(theaterId) && !REGAL_IDS_L.has(theaterId) && movie.showtimesForThisTheater
                            .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
                            .map((showtime, index) => {
                              const time = new Date(showtime.start_at).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit'
                              });
                              return (
                                <span key={index} className="showtime-badge">
                                  {time}
                                </span>
                              );
                            })}
                          <span className="showtime-actions">
                            {/* Tickets routes to OUR film page (Josh 8/10, shipped with
                                the Light Industry batch): the film page carries the
                                per-showtime Buy Tickets external links — visitors see
                                synopsis/poster/all dates before leaving the site. Button
                                still only renders when a booking link exists. */}
                            {getTicketLink(firstShowtime?.booking_link, theaterId) ? (
                              <Link
                                to={`/films/${movie.slug || slugify(cleanTitle, yearToDisplay)}`}
                                className="action-btn tickets-btn"
                              >
                                Tickets
                              </Link>
                            ) : null}
                            {firstShowtime && (
                              <a 
                                href={createGoogleCalendarLink({
                                  title: cleanTitle,
                                  startTime: firstShowtime.start_at,
                                  location: theater ? theater.name : 'Unknown Theater'
                                })}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="action-btn calendar-btn"
                              >
                                Add to Calendar
                              </a>
                            )}
                          </span>
                        </div>
                        </div>
                      </div>
                    );
                  });

              const allListItems = [
                ...standaloneListEntries.map(([theaterId, theaterData]) => ({
                  sortKey: theaterData.theaterName,
                  render: () => {
                    const theater = THEATERS.find(t => t.id === theaterId);
                    return (
                      <div key={theaterId} className="theater-group">
                        <h2 className="theater-header">
                          <Link
                            to={`/theater/${theater?.slug || theaterId}`}
                            style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            {theaterData.theaterName}
                          </Link>
                        </h2>
                        <div className={`movie-cards-grid ${compactMode ? "compact-list" : ""}`}>
                          {renderListMovies(theaterId, theaterData)}
                        </div>
                      </div>
                    );
                  }
                })),
                ...(amcListEntries.length > 0 ? [{
                  sortKey: 'AMC',
                  render: () => {
                    const amcMerged = {};
                    amcListEntries.forEach(([tId, tData]) => {
                      Object.values(tData.movies).forEach(movie => {
                        if (!amcMerged[movie.movie_id]) {
                          amcMerged[movie.movie_id] = { ...movie, showtimesForThisTheater: [], primaryTheaterId: tId };
                        }
                        amcMerged[movie.movie_id].showtimesForThisTheater.push(...movie.showtimesForThisTheater);
                      });
                    });
                    return (
                      <div key="amc-chain-list" className="chain-section">
                        <h2 className="theater-header chain-header">AMC</h2>
                        <p style={{ fontSize: '0.78rem', color: '#a89e90', fontStyle: 'italic', margin: '0 0 12px 0' }}>
                          Click title to see participating theaters
                        </p>
                        <div className={`movie-cards-grid ${compactMode ? "compact-list" : ""}`}>
                          {Object.values(amcMerged)
                            .sort((a, b) => a.title.localeCompare(b.title))
                            .map(movie => renderListMovies(movie.primaryTheaterId, { movies: { [movie.movie_id]: movie } }))
                          }
                        </div>
                      </div>
                    );
                  }
                }] : []),
                ...(regalListEntries.length > 0 ? [{
                  sortKey: 'Regal',
                  render: () => {
                    const regalMerged = {};
                    regalListEntries.forEach(([tId, tData]) => {
                      Object.values(tData.movies).forEach(movie => {
                        if (!regalMerged[movie.movie_id]) {
                          regalMerged[movie.movie_id] = { ...movie, showtimesForThisTheater: [], primaryTheaterId: tId };
                        }
                        regalMerged[movie.movie_id].showtimesForThisTheater.push(...movie.showtimesForThisTheater);
                      });
                    });
                    return (
                      <div key="regal-chain-list" className="chain-section">
                        <h2 className="theater-header chain-header">Regal</h2>
                        <p style={{ fontSize: '0.78rem', color: '#a89e90', fontStyle: 'italic', margin: '0 0 12px 0' }}>
                          Click title to see participating theaters
                        </p>
                        <div className={`movie-cards-grid ${compactMode ? "compact-list" : ""}`}>
                          {Object.values(regalMerged)
                            .sort((a, b) => a.title.localeCompare(b.title))
                            .map(movie => renderListMovies(movie.primaryTheaterId, { movies: { [movie.movie_id]: movie } }))
                          }
                        </div>
                      </div>
                    );
                  }
                }] : []),
              ];
              // BY TITLE (9/2, Josh): ONE card per film, alphabetical, using the
              // same card as the venue-grouped view. Multi-venue films collapse
              // into a single card that links onward for the full list.
              if (listSort === 'title') {
                const byFilm = {};
                Object.entries(theaterMovies).forEach(([tId, tData]) => {
                  const th = THEATERS.find((t) => t.id === tId);
                  Object.values(tData.movies).forEach((movie) => {
                    const rs = reviewedMovies[movie.movie_id];
                    if (isExclusionActive(rs)) return;
                    if (!byFilm[movie.movie_id]) {
                      byFilm[movie.movie_id] = {
                        primaryId: tId,
                        data: { theaterName: tData.theaterName, movies: { [movie.movie_id]: { ...movie, showtimesForThisTheater: [] } } },
                        venues: [],
                      };
                    }
                    const bucket = byFilm[movie.movie_id];
                    bucket.data.movies[movie.movie_id].showtimesForThisTheater.push(...movie.showtimesForThisTheater);
                    bucket.venues.push({ id: tId, slug: th?.slug, name: tData.theaterName, count: movie.showtimesForThisTheater.length });
                  });
                });
                const entries = Object.entries(byFilm).map(([mid, b]) => {
                  const m = b.data.movies[mid];
                  const { cleanTitle } = displayTitleYear(m, reviewedMovies[mid]);
                  return { mid, b, sortKey: (cleanTitle || '').toLowerCase() };
                });
                if (!entries.length) return null;
                return (
                  <div className={`movie-cards-grid ${compactMode ? 'compact-list' : ''}`}>
                    {entries.sort((a, b) => a.sortKey.localeCompare(b.sortKey)).map(({ mid, b }) => (
                      <div key={mid} style={{ display: 'contents' }}>
                        {renderListMovies(b.primaryId, b.data, b.venues)}
                      </div>
                    ))}
                  </div>
                );
              }

              return allListItems
                .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
                .map(item => item.render());
            })()}
          </div>
          </div>
      </div>
      )}
      <form
        id="newsletter"
        onSubmit={handleNewsletterSubmit}
        style={{ maxWidth: '420px', margin: '0 auto 30px auto', padding: '20px', background: '#faf7f2', border: '1px solid #d4cec4', borderRadius: '8px', textAlign: 'center', scrollMarginTop: '20px' }}
      >
        <h3 style={{ fontFamily: "'Cinzel', serif", fontSize: '1.1rem', color: '#1a1714', margin: '0 0 6px 0' }}>
          Get the Newsletter
        </h3>
        <p style={{ fontSize: '0.85rem', color: '#6b6255', margin: '0 0 14px 0' }}>
          Weekly highlights of the best revival screenings in NYC.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="email"
            value={newsletterEmail}
            onChange={(e) => setNewsletterEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
            disabled={newsletterSubmitting}
            style={{ flex: 1, padding: '10px 12px', border: '1px solid #d4cec4', borderRadius: '4px', fontSize: '0.9rem', background: '#fff', color: '#2a2520' }}
          />
          <button
            type="submit"
            disabled={newsletterSubmitting || !newsletterEmail}
            style={{
              padding: '10px 18px', background: '#8b6914', color: '#fff', border: 'none',
              borderRadius: '4px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer',
              opacity: newsletterSubmitting || !newsletterEmail ? 0.6 : 1,
              fontFamily: "'Inter', sans-serif", whiteSpace: 'nowrap',
            }}
          >
            {newsletterSubmitting ? '...' : 'Subscribe'}
          </button>
        </div>
        {newsletterMessage && (
          <p style={{ marginTop: '10px', fontSize: '0.85rem', color: newsletterMessage.type === 'success' ? '#4CAF50' : '#cc4444' }}>
            {newsletterMessage.text}
          </p>
        )}
        {/* Cross-sell (Josh 9/2): a fresh subscriber without an account gets a
            one-click offer to create one, email prefilled. Logged-in users and
            error states never see it. */}
        {newsletterMessage?.type === 'success' && !user && subscribedEmail && (
          <button
            type="button"
            onClick={() => setShowFooterAuthModal(true)}
            style={{
              marginTop: '10px', padding: '9px 16px', background: 'none',
              border: '1px solid #8b6914', color: '#8b6914', borderRadius: '4px',
              fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Save films and get alerts when they play: create a free account
          </button>
        )}
      </form>
      {showFooterAuthModal && (
        <AuthModal
          isOpen
          onClose={() => setShowFooterAuthModal(false)}
          initialEmail={subscribedEmail}
          initialMode="signup"
        />
      )}
          
          <div style={{ marginBottom: '40px', paddingTop: '30px', borderTop: '1px solid #2a2520', textAlign: 'left', paddingLeft: '20px' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '20px', color: '#1a1714' }}>Browse by Theater</h3>
            {LOCATIONS.map(location => {
              const theatersInLocation = THEATERS.filter(t => THEATER_LOCATIONS[t.id] === location);
              if (theatersInLocation.length === 0) return null;
              return (
                <div key={location} style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999', marginBottom: '8px' }}>
                    {location}
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {theatersInLocation.map(theater => (
                      <Link
                        key={theater.id}
                        to={`/theater/${theater.slug}`}
                        style={{ fontSize: '0.85rem', color: '#8b6914', textDecoration: 'none', padding: '4px 10px', border: '1px solid #2a2520', borderRadius: '20px', whiteSpace: 'nowrap' }}
                      >
                        {theater.name}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{
            textAlign: 'center',
            padding: '40px 20px 20px',
            marginTop: '60px',
            marginBottom: '20px',
            borderTop: '1px solid #d4cec4',
            maxWidth: '600px',
            margin: '60px auto 20px'
          }}>
            <div style={{
              width: '32px',
              height: '2px',
              background: '#d4a843',
              margin: '0 auto 24px',
              borderRadius: '1px'
            }} />
            <span style={{ fontSize: '0.9rem', color: '#2a2520', fontFamily: "'Cinzel', Georgia, serif", letterSpacing: '0.03em' }}>
              Brought to you by <a href="https://aboutitfilms.com" target="_blank" rel="noopener noreferrer" style={{ color: '#8b6914', textDecoration: 'none' }}>About It Films</a>
            </span>
            <p style={{ fontSize: '0.85rem', color: '#6b6255', lineHeight: '1.6', margin: '16px 0' }}>
              🎞️ <strong>Support us by grabbing a FlickMark</strong> — real 35mm film bookmarks featuring iconic frames from Le Voyage dans la Lune.<br />
              Shop on <a href="https://www.etsy.com/listing/1791662027/movie-bookmark-gift-for-movie-lover" target="_blank" rel="noopener noreferrer" style={{ color: '#8b6914', textDecoration: 'none' }} onClick={() => window.gtag('event', 'shop_click', { destination: 'etsy', location: 'footer', transport_type: 'beacon' })}>Etsy</a>
            </p>
            <p style={{ fontSize: '0.8rem', color: '#a89e90', lineHeight: '1.6', margin: '16px 0 20px' }}>
              Feature requests, bugs, or just want to say hello?<br />
              <a href="mailto:filmrevivalnyc@gmail.com" style={{ color: '#8b6914', textDecoration: 'none' }}>filmrevivalnyc@gmail.com</a>
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', fontSize: '0.82rem' }}>
              <a href="/changelog" style={{ color: '#8b6914', textDecoration: 'none', fontWeight: '500' }}>What's New</a>
              <span style={{ color: '#d4cec4' }}>|</span>
              <a href="/faq" style={{ color: '#8b6914', textDecoration: 'none', fontWeight: '500' }}>FAQ</a>
              <span style={{ color: '#d4cec4' }}>·</span>
              <a href="/submit-screening" style={{ color: '#8b6914', textDecoration: 'none', fontWeight: '500' }}>Submit Your Screening</a>
            </div>
          </div>

    </>
  );
}

export default CalendarView;
